import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatCard, TargetRing } from "@/components/Stat";
import { LeadLinkQR } from "@/components/LeadLinkQR";
import { NotificationBell } from "@/components/NotificationBell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Flame, CalendarClock, AlertTriangle, Sparkles, UserPlus, Link2 } from "lucide-react";
import { toast } from "sonner";

export default function RMDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/dashboard/rm").then((r) => setS(r.data));
  }, []);

  if (!s) return <div className="text-slate-400">Loading…</div>;
  const stages = s.stage_counts || {};
  const shareLink = `${window.location.origin}/apply/${user.public_token}`;
  const copyLink = () => { navigator.clipboard.writeText(shareLink); toast.success("Lead form link copied — share it with customers!"); };

  return (
    <div>
      <PageHeader
        title={`Hello, ${user.name.split(" ")[0]} 👋`}
        subtitle="Here's your loan pipeline at a glance."
        actions={
          <div className="flex gap-2">
            <NotificationBell />
            <Button variant="outline" data-testid="copy-lead-link" onClick={copyLink}><Link2 size={16} className="mr-2" />Share Invitation</Button>
            <LeadLinkQR url={shareLink} name={user.name} label="QR" testid="rm-qr-btn" />
            <Button data-testid="quick-add-customer" onClick={() => navigate("/app/add-customer")} className="bg-[#0F4C81] hover:bg-[#0A3A66]"><UserPlus size={16} className="mr-2" />Add Customer</Button>
          </div>
        }
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-6">
        <StatCard testid="stat-my-customers" label="My Customers" value={s.total_customers} icon={Users} accent="blue" />
        <StatCard testid="stat-today-followups" label="Today's Follow-ups" value={s.today_followups} icon={CalendarClock} accent="green" />
        <StatCard testid="stat-hot-leads" label="Hot Leads" value={s.hot_leads} icon={Flame} accent="rose" />
        <StatCard testid="stat-mela-invited" label="Mela Invites" value={s.mela_invited} icon={Sparkles} accent="violet" />
        <StatCard testid="stat-invite-leads" label="From My Link" value={s.invite_leads} icon={Link2} accent="blue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 flex flex-col items-center justify-center">
          <div className="text-sm font-semibold text-slate-600 mb-4 self-start">Monthly Disbursal Target</div>
          <TargetRing value={s.disbursed_amount} target={s.monthly_target} />
          <div className="mt-4 text-center">
            <div className="text-lg font-bold text-slate-900">{inr(s.disbursed_amount)} <span className="text-slate-400 font-normal">/ {inr(s.monthly_target)}</span></div>
            <div className="text-xs text-slate-500">Disbursed this month</div>
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2">
          <div className="text-sm font-semibold text-slate-600 mb-4">Pipeline Breakdown</div>
          <div className="space-y-3">
            {Object.entries(stages).map(([stage, count]) => {
              const max = Math.max(...Object.values(stages), 1);
              return (
                <div key={stage} className="flex items-center gap-3" data-testid={`pipeline-${stage}`}>
                  <div className="w-40 text-sm text-slate-600 shrink-0">{stage}</div>
                  <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#0F4C81] to-emerald-500 rounded-full" style={{ width: `${(count / max) * 100}%` }} />
                  </div>
                  <div className="w-8 text-sm font-bold text-slate-700 text-right">{count}</div>
                </div>
              );
            })}
          </div>
          {s.overdue_followups > 0 && (
            <div className="mt-5 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3" data-testid="overdue-alert">
              <AlertTriangle size={16} /> You have <b>{s.overdue_followups}</b> overdue follow-ups.
              <button onClick={() => navigate("/app/followups")} className="ml-auto underline font-semibold">Review</button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
